/**
 * AtlasInviteModule — INVITE LINKS and DISCORD (2026-09-24). The rules are in lib/discord.ts and
 * server/src/reducers/invites.ts.
 *
 *   THE WINDOW (menu bar → Invite): make a link (48 h, ten uses) that brings a friend to where you stand, copy it,
 *     revoke it; inside a Discord Activity also "Share on Discord" (the link as an Activity share) and Discord's own
 *     invite dialog.
 *   THE ARRIVAL: a page opened with `?join=<token>` (or launched from a Discord share with `?custom_id=<token>`)
 *     keeps the token for the session and redeems it once the world has loaded — the server stands you 2.5 m in front
 *     of your friend. The token leaves the address bar at once (a reload never re-redeems it).
 *   PRESENCE (an Activity only): every 30 s — the town you are near and what you are doing.
 * Levers: `__invite`.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import { townList } from '../lib/town_names.js';
import {
  inDiscordActivity, launchJoinToken, inviteUrl, discordState, setDiscordPresence, shareInviteOnDiscord, openDiscordInviteDialog,
} from '../lib/discord.js';
import { el, btn, head, muted, card, chip, act, debounced, ms } from './town/town_ui.js';

interface InviteRow { token: string; inviter: { toHexString(): string }; worldId: bigint; uses: number; maxUses: number; createdAt: { microsSinceUnixEpoch: bigint }; expiresAt: { microsSinceUnixEpoch: bigint } }

const PENDING_KEY = 'atlas:pending-join';
const PRESENCE_MS = 30_000;

export class AtlasInviteModule implements IModule {
  readonly name = 'AtlasInvite';
  private win: UiWindow | null = null;
  private subbed = false;
  private pending: string | null = null;
  private redeemed: string | null = null;
  private lastResult = '';
  private presenceTimer: number | null = null;
  private startMs = Date.now();
  private readonly rerender = debounced(() => { if (this.win?.isOpen()) this.render(); }, 250);

  init(_app: App): void {
    // Take the token off the address bar at once and keep it for this session.
    const t = launchJoinToken();
    try {
      if (t) sessionStorage.setItem(PENDING_KEY, t);
      this.pending = t ?? sessionStorage.getItem(PENDING_KEY);
    } catch { this.pending = t; }
    if (t) {
      try {
        const u = new URL(location.href);
        u.searchParams.delete('join');
        if (!inDiscordActivity()) u.searchParams.delete('custom_id');
        history.replaceState(history.state, '', u.toString());
      } catch { /* a sandboxed frame */ }
    }
    bus.on('atlas:open-invite', () => this.open());
    bus.on('atlas:account-ready', () => this.subscribe());
    bus.on('atlas:loading-done', () => { void this.redeemPending(); });
    // Also once the world is simply THERE (a resume, a page whose load screen went another way): poll until tried.
    const poll = window.setInterval(() => {
      if (!this.pending || this.redeemed === this.pending) { if (this.redeemed !== null || !this.pending) window.clearInterval(poll); return; }
      if (atlas.currentWorldId !== null && !atlas.loadingGate && atlas.conn) void this.redeemPending();
    }, 2000);
    if (atlas.conn) this.subscribe();
    if (inDiscordActivity()) {
      this.presenceTimer = window.setInterval(() => { void this.presence(); }, PRESENCE_MS);
      window.setTimeout(() => { void this.presence(); }, 4000);
    }
    (window as unknown as { __invite?: unknown }).__invite = {
      state: () => ({ pending: this.pending, redeemed: this.redeemed, lastResult: this.lastResult, invites: this.mine().map((i) => i.token), discord: discordState() }),
      open: () => this.open(),
      create: () => this.create(),
      url: (token: string) => inviteUrl(token),
      redeem: (token: string) => { this.pending = token; return this.redeemPending(); },
    };
  }

  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try {
      conn.db.playerInvite.onInsert(() => this.rerender());
      conn.db.playerInvite.onUpdate(() => this.rerender());
      conn.db.playerInvite.onDelete(() => this.rerender());
      conn.subscriptionBuilder().subscribe(['SELECT * FROM player_invite']);
    } catch { /* pre-publish */ }
  }

  private mine(): InviteRow[] {
    const conn = atlas.conn;
    const me = atlas.identityHex;
    if (!conn || !me) return [];
    const now = Date.now();
    const out: InviteRow[] = [];
    try {
      for (const r of conn.db.playerInvite.iter() as Iterable<InviteRow>) {
        if (r.inviter.toHexString() === me && ms(r.expiresAt) > now) out.push(r);
      }
    } catch { /* not subscribed */ }
    return out.sort((a, b) => ms(b.createdAt) - ms(a.createdAt));
  }

  private open(): void {
    if (!this.win) {
      this.win = makeWindow({ id: 'invite', title: 'Invite a friend', icon: 'people', width: 440, minWidth: 340 });
    }
    this.win.setOpen(true);
    this.render();
  }

  private async create(): Promise<string | null> {
    const conn = atlas.conn;
    if (!conn) return null;
    const before = new Set(this.mine().map((i) => i.token));
    const ok = await act(conn.reducers.createInvite({ maxUses: 10 }), '');
    if (!ok) return null;
    for (let i = 0; i < 40; i++) {
      const fresh = this.mine().find((r) => !before.has(r.token));
      if (fresh) { this.render(); return fresh.token; }
      await new Promise((r) => setTimeout(r, 100));
    }
    return null;
  }

  private async copy(text: string): Promise<void> {
    try { await navigator.clipboard.writeText(text); bus.emit('atlas:toast', { text: 'Invite link copied', kind: 'success', ms: 2400 }); }
    catch { bus.emit('atlas:toast', { text: `Copy this link: ${text}`, kind: 'info', ms: 9000 }); }
  }

  private render(): void {
    const body = this.win?.body;
    if (!body) return;
    body.replaceChildren();
    const wrap = el('div', 'display:flex;flex-direction:column;gap:10px;padding:12px 14px');
    wrap.appendChild(muted('A link brings a friend to where you stand: they open it, enter the world, and arrive beside you (while you are online and in the same world). Links last 48 hours and can be used ten times.'));
    const row = el('div', 'display:flex;gap:8px;flex-wrap:wrap');
    row.appendChild(btn('Make a link', async () => { const t = await this.create(); if (t) void this.copy(inviteUrl(t)); }, true));
    if (inDiscordActivity()) {
      row.appendChild(btn('Share on Discord', async () => {
        const t = this.mine()[0]?.token ?? await this.create();
        if (!t) return;
        const ok = await shareInviteOnDiscord(t, 'Come find me in Cosmos');
        bus.emit('atlas:toast', { text: ok ? 'Shared' : 'Discord did not share it', kind: ok ? 'success' : 'error', ms: 3000 });
      }));
      row.appendChild(btn('Discord invite…', () => { void openDiscordInviteDialog(); }));
    }
    wrap.appendChild(row);
    wrap.appendChild(head('Your links'));
    const list = this.mine();
    if (list.length === 0) wrap.appendChild(muted('None yet.'));
    for (const inv of list) {
      const c = card();
      const url = inviteUrl(inv.token);
      const line = el('div', 'font:12px var(--ax-mono);color:var(--ax-text);word-break:break-all', url);
      c.appendChild(line);
      const meta = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap');
      meta.appendChild(chip(`${inv.uses}/${inv.maxUses} used`, inv.uses >= inv.maxUses ? 'bad' : 'info'));
      const left = Math.max(0, ms(inv.expiresAt) - Date.now());
      meta.appendChild(chip(`${Math.floor(left / 3_600_000)} h left`));
      meta.appendChild(btn('Copy', () => { void this.copy(url); }));
      meta.appendChild(btn('Revoke', () => { void act(atlas.conn?.reducers.revokeInvite({ token: inv.token }), 'Link revoked'); }));
      c.appendChild(meta);
      wrap.appendChild(c);
    }
    if (inDiscordActivity()) {
      const d = discordState();
      wrap.appendChild(muted(`Discord: presence ${String(d.presence)}${d.lastError ? ` — ${String(d.lastError)}` : ''}`));
    } else {
      wrap.appendChild(muted('Paste the link into Discord or anywhere; Discord shows a preview card for it.'));
    }
    body.appendChild(wrap);
  }

  /** Redeem the session's pending token once the world is loaded (and the inviter's world is this one). */
  private async redeemPending(): Promise<string> {
    const token = this.pending;
    const conn = atlas.conn;
    if (!token || !conn || this.redeemed === token) return this.lastResult;
    this.redeemed = token;
    try { sessionStorage.removeItem(PENDING_KEY); } catch { /* private */ }
    try {
      await conn.reducers.redeemInvite({ token });
      this.lastResult = 'ok';
      bus.emit('atlas:toast', { text: 'You arrive beside your friend', kind: 'success', ms: 4000 });
    } catch (e) {
      this.lastResult = String((e as Error)?.message ?? e).replace(/^SenderError:\s*/, '');
      bus.emit('atlas:toast', { text: `Invite: ${this.lastResult}`, kind: 'error', ms: 6000 });
    }
    return this.lastResult;
  }

  /** Rich presence: the nearest town and a word on what you are doing. */
  private async presence(): Promise<void> {
    if (atlas.currentWorldId === null) return;
    let near = '';
    let bd = 3000;
    for (const t of townList()) {
      const d = Math.hypot(t.x - atlas.localX, t.z - atlas.localZ);
      if (d < bd) { bd = d; near = t.name; }
    }
    const doing = atlas.spaceFlight ? 'Flying in space' : atlas.hoverRide ? 'Riding a hovercraft' : atlas.interiorId ? 'Indoors' : 'Exploring';
    await setDiscordPresence({ details: near ? `Near ${near}` : 'In the wilds of the Commons', state: doing, startMs: this.startMs });
  }

  dispose(): void {
    if (this.presenceTimer !== null) window.clearInterval(this.presenceTimer);
    this.win?.root.remove();
  }
}
